import { useEffect, useRef, useState } from 'react';

const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

const isNumeric = (v) => typeof v === 'number' && Number.isFinite(v);

const prefersReducedMotion = () =>
    typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// Anima um número do valor atualmente exibido até `value` (easeOutCubic, rAF).
// - 1ª exibição: parte de 0; mudanças de `value`: partem do valor em tela.
// - `decimals`: casas do valor intermediário (2 = centavos inteiros, evita
//   ruído de ponto flutuante); o último frame é sempre exatamente `value`.
// - Valor não numérico (null/NaN/string) é devolvido como veio, sem animar.
// - `prefers-reduced-motion` => devolve o valor final direto.
export default function useCountUp(value, { duration = 900, decimals = 0 } = {}) {
    const numeric = isNumeric(value);
    const skip = !numeric || prefersReducedMotion() || duration <= 0;
    const [display, setDisplay] = useState(skip ? value : 0);
    const displayRef = useRef(skip ? value : 0);

    useEffect(() => {
        if (!isNumeric(value)) return undefined;
        if (prefersReducedMotion() || duration <= 0) {
            displayRef.current = value;
            setDisplay(value);
            return undefined;
        }
        const from = isNumeric(displayRef.current) ? displayRef.current : 0;
        if (from === value) {
            setDisplay(value);
            return undefined;
        }
        const factor = Math.pow(10, decimals);
        const start = performance.now();
        let raf;
        const tick = (now) => {
            const t = Math.min(1, (now - start) / duration);
            if (t >= 1) {
                displayRef.current = value;
                setDisplay(value);
                return;
            }
            const current = Math.round((from + (value - from) * easeOutCubic(t)) * factor) / factor;
            displayRef.current = current;
            setDisplay(current);
            raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf);
    }, [value, duration, decimals]);

    return numeric ? display : value;
}
